<div class="am-wrap">
  <div *ngIf="snack" class="am-snack" [class.error]="snack.kind === 'error'" [class.success]="snack.kind === 'success'">
    {{ snack.text }}
  </div>

  <header class="am-header">
    <div>
      <h1>{{ 'assetMgmt.title' | translate }}</h1>
      <p class="am-sub">{{ 'assetMgmt.subtitle' | translate }}</p>
    </div>
    <div class="am-actions">
      <button class="btn ghost" (click)="loadDemoData()" [disabled]="seedingDemo">
        <i class="fa fa-database"></i> {{ seedingDemo ? ('assetMgmt.loadingDemo' | translate) : ('assetMgmt.loadDemo' | translate) }}
      </button>
      <button class="btn ghost" (click)="openMapEditor()">
        <i class="fa fa-map"></i> {{ 'assetMgmt.openMap' | translate }}
      </button>
      <button class="btn ghost" (click)="openImportModal()">
        <i class="fa fa-upload"></i> {{ 'assetMgmt.import' | translate }}
      </button>
      <button class="btn ghost" (click)="exportLayer('geojson')">
        <i class="fa fa-download"></i> GeoJSON
      </button>
      <button class="btn ghost" (click)="exportLayer('kml')">
        <i class="fa fa-download"></i> KML
      </button>
      <button class="btn primary" (click)="openLayerModal()">
        <i class="fa fa-plus"></i> {{ 'assetMgmt.newLayer' | translate }}
      </button>
      <button class="btn primary" (click)="openAssetModal()">
        <i class="fa fa-plus"></i> {{ 'assetMgmt.newAsset' | translate }}
      </button>
      <button class="btn primary" (click)="openMapEditorForCreate(searchForm.value.layerId)">
        <i class="fa fa-pencil-square-o"></i> {{ 'assetMgmt.drawOnMap' | translate }}
      </button>
    </div>
  </header>

  <!-- Health banner -->
  <div *ngIf="health && !health.postgis" class="am-warn">
    <i class="fa fa-exclamation-triangle"></i>
    {{ 'assetMgmt.postgisMissing' | translate }}
  </div>

  <!-- Dashboard cards -->
  <section class="am-grid am-grid-4">
    <div class="am-card stat">
      <div class="stat-label">{{ 'assetMgmt.totalAssets' | translate }}</div>
      <div class="stat-value">{{ stats?.total_assets ?? 0 }}</div>
      <div class="stat-foot">
        <span>{{ 'assetMgmt.layersLabel' | translate }}: {{ stats?.layers ?? 0 }}</span>
      </div>
    </div>
    <div class="am-card stat">
      <div class="stat-label">{{ 'assetMgmt.underground' | translate }}</div>
      <div class="stat-value">{{ classificationCount('underground') }}</div>
      <div class="stat-foot">
        <span>{{ 'assetMgmt.overgroundLabel' | translate }}: {{ classificationCount('overground') }}</span>
      </div>
    </div>
    <div class="am-card stat">
      <div class="stat-label">{{ 'assetMgmt.activeAssets' | translate }}</div>
      <div class="stat-value">{{ statusCount('active') }}</div>
      <div class="stat-foot">
        <span>{{ 'assetMgmt.decommissioned' | translate }}: {{ statusCount('decommissioned') }}</span>
      </div>
    </div>
    <div class="am-card stat">
      <div class="stat-label">{{ 'assetMgmt.relationships' | translate }}</div>
      <div class="stat-value">{{ stats?.relationships ?? 0 }}</div>
      <div class="stat-foot">
        <span>{{ 'assetMgmt.versions' | translate }}: {{ stats?.versions ?? 0 }}</span>
      </div>
    </div>
  </section>

  <!-- Layers -->
  <section class="am-card">
    <div class="am-card-head">
      <h2>{{ 'assetMgmt.layers' | translate }}</h2>
      <span class="muted">{{ layers.length }} {{ 'assetMgmt.totalLabel' | translate }}</span>
    </div>
    <div *ngIf="loadingLayers" class="am-loading">{{ 'assetMgmt.loading' | translate }}…</div>
    <div *ngIf="!loadingLayers" class="am-layer-grid">
      <div class="am-layer" *ngFor="let l of layers; trackBy: trackById">
        <div class="layer-meta">
          <div class="layer-name">{{ l.name }}</div>
          <div class="layer-code">{{ l.code }}</div>
          <div class="layer-tags">
            <span class="chip" [class.under]="l.classification === 'underground'">
              {{ ('assetMgmt.classification.' + l.classification) | translate }}
            </span>
            <span class="chip">{{ l.asset_category }}</span>
            <span class="chip">{{ l.geometry_type }}</span>
          </div>
        </div>
        <div class="layer-actions">
          <button class="link" (click)="openLayerModal(l)" [title]="'assetMgmt.editLayer' | translate">
            <i class="fa fa-edit"></i>
          </button>
          <button class="link" (click)="deleteLayer(l)">
            <i class="fa fa-trash"></i>
          </button>
        </div>
      </div>
      <div *ngIf="layers.length === 0" class="empty">
        {{ 'assetMgmt.noLayers' | translate }}
      </div>
    </div>
  </section>

  <!-- Assets -->
  <section class="am-card">
    <div class="am-card-head">
      <h2>{{ 'assetMgmt.assets' | translate }}</h2>
      <span class="muted">{{ totalCount }} {{ 'assetMgmt.shown' | translate }}</span>
    </div>

    <form [formGroup]="searchForm" class="am-filters">
      <input type="text" placeholder="{{ 'assetMgmt.searchPlaceholder' | translate }}" formControlName="search" />
      <select formControlName="layerId">
        <option [ngValue]="null">{{ 'assetMgmt.allLayers' | translate }}</option>
        <option *ngFor="let l of layers" [ngValue]="l.id">{{ l.name }}</option>
      </select>
      <select formControlName="classification">
        <option [ngValue]="null">{{ 'assetMgmt.allClassifications' | translate }}</option>
        <option *ngFor="let c of classifications" [value]="c">
          {{ ('assetMgmt.classification.' + c) | translate }}
        </option>
      </select>
      <select formControlName="assetCategory">
        <option [ngValue]="null">{{ 'assetMgmt.allCategories' | translate }}</option>
        <option *ngFor="let c of assetCategories" [value]="c">{{ c }}</option>
      </select>
      <select formControlName="status">
        <option [ngValue]="null">{{ 'assetMgmt.allStatuses' | translate }}</option>
        <option *ngFor="let s of statuses" [value]="s">{{ s }}</option>
      </select>
      <label class="am-check">
        <input type="checkbox" formControlName="includeDeleted" />
        {{ 'assetMgmt.includeDeleted' | translate }}
      </label>
    </form>

    <div *ngIf="loadingAssets" class="am-loading">{{ 'assetMgmt.loading' | translate }}…</div>

    <div *ngIf="!loadingAssets" class="am-table-wrap">
      <table class="am-table">
        <thead>
          <tr>
            <th>{{ 'assetMgmt.code' | translate }}</th>
            <th>{{ 'assetMgmt.name' | translate }}</th>
            <th>{{ 'assetMgmt.layer' | translate }}</th>
            <th>{{ 'assetMgmt.classificationLabel' | translate }}</th>
            <th>{{ 'assetMgmt.category' | translate }}</th>
            <th>{{ 'assetMgmt.status' | translate }}</th>
            <th>{{ 'assetMgmt.depth' | translate }}</th>
            <th>{{ 'assetMgmt.version' | translate }}</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          <tr *ngFor="let a of assets; trackBy: trackById">
            <td><code>{{ a.properties.asset_code || a.id }}</code></td>
            <td>{{ a.properties.name || '—' }}</td>
            <td>{{ layerName(a.properties.layer_id) }}</td>
            <td>
              <span class="chip" [class.under]="a.properties.classification === 'underground'">
                {{ a.properties.classification }}
              </span>
            </td>
            <td>{{ a.properties.asset_category }}</td>
            <td>
              <span class="chip status">{{ a.properties.status }}</span>
              <span class="chip danger" *ngIf="a.properties.is_deleted">{{ 'assetMgmt.deleted' | translate }}</span>
            </td>
            <td>{{ a.properties.depth_m ?? '—' }}</td>
            <td>v{{ a.properties.current_version || 1 }}</td>
            <td class="actions">
              <ng-container *ngIf="!a.properties.is_deleted">
                <button class="link" (click)="openAssetModal(a)" [title]="'assetMgmt.edit' | translate">
                  <i class="fa fa-edit"></i>
                </button>
                <button class="link" (click)="editAssetGeometryOnMap(a)" [title]="'assetMgmt.editGeometryOnMap' | translate">
                  <i class="fa fa-pencil"></i>
                </button>
                <button class="link" (click)="openVersionsModal(a)" *ngIf="(a.properties.current_version || 1) > 1" [title]="'assetMgmt.versionHistory' | translate">
                  <i class="fa fa-history"></i>
                </button>
                <button class="link danger" (click)="deleteAsset(a)" [title]="'assetMgmt.delete' | translate">
                  <i class="fa fa-trash"></i>
                </button>
              </ng-container>
              <button class="link" *ngIf="a.properties.is_deleted" (click)="restoreAsset(a)" [title]="'assetMgmt.restore' | translate">
                <i class="fa fa-undo"></i> {{ 'assetMgmt.restore' | translate }}
              </button>
            </td>
          </tr>
          <tr *ngIf="assets.length === 0">
            <td colspan="9" class="empty">{{ 'assetMgmt.noAssets' | translate }}</td>
          </tr>
        </tbody>
      </table>
    </div>

    <div class="am-pager">
      <button class="btn ghost" (click)="changePage(-1)" [disabled]="pageIndex === 0">
        <i class="fa fa-chevron-left"></i>
      </button>
      <span>{{ 'assetMgmt.page' | translate }} {{ pageIndex + 1 }}</span>
      <button class="btn ghost" (click)="changePage(1)" [disabled]="(assets.length || 0) < pageSize">
        <i class="fa fa-chevron-right"></i>
      </button>
    </div>
  </section>

  <!-- ERP integration -->
  <section class="am-card">
    <div class="am-card-head">
      <h2>ERP Integration</h2>
      <span class="muted">{{ erpIntegrations.length }} connectors</span>
    </div>

    <form [formGroup]="erpForm" class="am-form two-col">
      <label>Provider
        <select formControlName="provider">
          <option value="sap">SAP</option>
          <option value="oracle">Oracle ERP</option>
          <option value="dynamics">MS Dynamics</option>
          <option value="odoo">Odoo</option>
          <option value="custom">Custom</option>
        </select>
      </label>
      <label>Connector Name <input formControlName="name" /></label>
      <label>Instance Key <input formControlName="instance_key" /></label>
      <label>Default Layer Code <input formControlName="default_layer_code" /></label>
      <label>
        Enabled
        <select formControlName="is_enabled">
          <option [ngValue]="true">Yes</option>
          <option [ngValue]="false">No</option>
        </select>
      </label>
      <label>
        Dry run
        <select formControlName="dry_run">
          <option [ngValue]="false">No (write changes)</option>
          <option [ngValue]="true">Yes (validate only)</option>
        </select>
      </label>
      <label class="full">
        ERP Sync Payload (JSON array of records)
        <textarea
          rows="7"
          formControlName="erp_payload"
          placeholder='[{"external_id":"EQ-102","layer_code":"machines","name":"Excavator 102","classification":"overground","asset_category":"equipment","status":"active","lng":77.4479,"lat":23.7831}]'
        ></textarea>
      </label>
    </form>

    <div class="am-modal-foot">
      <button class="btn ghost" (click)="saveErpIntegration()" [disabled]="saving || loadingErp">
        Save Connector
      </button>
      <button class="btn primary" (click)="runErpSync()" [disabled]="syncingErp">
        {{ syncingErp ? 'Syncing...' : 'Run ERP Sync' }}
      </button>
      <button class="btn ghost" (click)="loadErpSyncRuns()">
        Refresh Sync Runs
      </button>
    </div>

    <div class="am-table-wrap" *ngIf="erpSyncRuns.length > 0">
      <table class="am-table">
        <thead>
          <tr>
            <th>Started</th>
            <th>Provider</th>
            <th>Status</th>
            <th>Total</th>
            <th>Created</th>
            <th>Updated</th>
            <th>Failed</th>
          </tr>
        </thead>
        <tbody>
          <tr *ngFor="let r of erpSyncRuns">
            <td>{{ r.started_at | date: 'short' }}</td>
            <td>{{ r.provider || '—' }}</td>
            <td><span class="chip status">{{ r.status }}</span></td>
            <td>{{ r.total_records }}</td>
            <td>{{ r.created_count }}</td>
            <td>{{ r.updated_count }}</td>
            <td>{{ r.failed_count }}</td>
          </tr>
        </tbody>
      </table>
    </div>
  </section>

  <!-- Audit feed -->
  <section class="am-card">
    <div class="am-card-head">
      <h2>{{ 'assetMgmt.recentActivity' | translate }}</h2>
      <button class="link" (click)="loadAudit()"><i class="fa fa-refresh"></i></button>
    </div>
    <div *ngIf="loadingAudit" class="am-loading">{{ 'assetMgmt.loading' | translate }}…</div>
    <ul *ngIf="!loadingAudit" class="am-audit">
      <li *ngFor="let a of audit">
        <span class="action">{{ a.action }}</span>
        <span class="muted"> · {{ a.entity }} #{{ a.asset_id || a.layer_id || '?' }}</span>
        <span class="muted right">{{ a.created_at | date: 'medium' }}</span>
      </li>
      <li *ngIf="audit.length === 0" class="empty">{{ 'assetMgmt.noActivity' | translate }}</li>
    </ul>
  </section>
</div>

<!-- Layer modal -->
<div class="am-modal" *ngIf="showLayerModal" (click)="closeLayerModal()">
  <div class="am-modal-box" (click)="$event.stopPropagation()">
    <h3>{{ (selectedLayer ? 'assetMgmt.editLayer' : 'assetMgmt.newLayer') | translate }}<ng-container *ngIf="selectedLayer"> — {{ selectedLayer.name }}</ng-container></h3>
    <form [formGroup]="layerForm" class="am-form">
      <label>{{ 'assetMgmt.code' | translate }} <input formControlName="code" [attr.disabled]="selectedLayer ? true : null" /></label>
      <label>{{ 'assetMgmt.name' | translate }} <input formControlName="name" /></label>
      <label>{{ 'assetMgmt.description' | translate }} <textarea formControlName="description" rows="2"></textarea></label>
      <label>{{ 'assetMgmt.classificationLabel' | translate }}
        <select formControlName="classification">
          <option *ngFor="let c of classifications" [value]="c">{{ c }}</option>
        </select>
      </label>
      <label>{{ 'assetMgmt.category' | translate }}
        <select formControlName="asset_category">
          <option *ngFor="let c of assetCategories" [value]="c">{{ c }}</option>
        </select>
      </label>
      <label>{{ 'assetMgmt.geometryType' | translate }}
        <select formControlName="geometry_type">
          <option *ngFor="let g of geometryTypes" [value]="g">{{ g }}</option>
        </select>
      </label>
      <label>SRID <input type="number" formControlName="srid" [attr.disabled]="selectedLayer ? true : null" /></label>
    </form>
    <div class="am-modal-foot">
      <button class="btn ghost" (click)="closeLayerModal()">{{ 'common.cancel' | translate }}</button>
      <button class="btn primary" (click)="saveLayer()" [disabled]="saving">{{ 'common.save' | translate }}</button>
    </div>
  </div>
</div>

<!-- Asset modal -->
<div class="am-modal" *ngIf="showAssetModal" (click)="closeAssetModal()">
  <div class="am-modal-box" (click)="$event.stopPropagation()">
    <h3>
      {{ (selectedAsset ? 'assetMgmt.editAsset' : 'assetMgmt.newAsset') | translate }}
    </h3>
    <form [formGroup]="assetForm" class="am-form two-col">
      <label>{{ 'assetMgmt.layer' | translate }}
        <select formControlName="layer_id">
          <option [ngValue]="null">--</option>
          <option *ngFor="let l of layers" [ngValue]="l.id">{{ l.name }} ({{ l.code }})</option>
        </select>
      </label>
      <label>{{ 'assetMgmt.code' | translate }}<input formControlName="asset_code" /></label>
      <label>{{ 'assetMgmt.name' | translate }}<input formControlName="name" /></label>
      <label>{{ 'assetMgmt.classificationLabel' | translate }}
        <select formControlName="classification">
          <option *ngFor="let c of classifications" [value]="c">{{ c }}</option>
        </select>
      </label>
      <label>{{ 'assetMgmt.category' | translate }}
        <select formControlName="asset_category">
          <option *ngFor="let c of assetCategories" [value]="c">{{ c }}</option>
        </select>
      </label>
      <label>{{ 'assetMgmt.status' | translate }}
        <select formControlName="status">
          <option *ngFor="let s of statuses" [value]="s">{{ s }}</option>
        </select>
      </label>
      <label>{{ 'assetMgmt.depth' | translate }} (m)<input type="number" step="0.01" formControlName="depth_m" /></label>
      <label>{{ 'assetMgmt.elevation' | translate }} (m)<input type="number" step="0.01" formControlName="elevation_m" /></label>
      <label>Lng<input type="number" step="0.000001" formControlName="lng" /></label>
      <label>Lat<input type="number" step="0.000001" formControlName="lat" /></label>
      <label class="full">{{ 'assetMgmt.description' | translate }}
        <textarea formControlName="description" rows="2"></textarea>
      </label>
    </form>
    <p class="muted small">{{ 'assetMgmt.geometryHint' | translate }}</p>
    <div class="am-modal-foot">
      <button class="btn ghost" (click)="closeAssetModal()">{{ 'common.cancel' | translate }}</button>
      <button class="btn primary" (click)="saveAsset()" [disabled]="saving">{{ 'common.save' | translate }}</button>
    </div>
  </div>
</div>

<!-- Import modal -->
<div class="am-modal" *ngIf="showImportModal" (click)="closeImportModal()">
  <div class="am-modal-box" (click)="$event.stopPropagation()">
    <h3>{{ 'assetMgmt.importTitle' | translate }}</h3>
    <div class="am-form">
      <label>{{ 'assetMgmt.layer' | translate }}
        <select [(ngModel)]="importLayerId" name="importLayer">
          <option [ngValue]="null">--</option>
          <option *ngFor="let l of layers" [ngValue]="l.id">{{ l.name }}</option>
        </select>
      </label>
      <label>{{ 'assetMgmt.format' | translate }}
        <select [(ngModel)]="importFormat" name="importFmt">
          <option value="geojson">GeoJSON</option>
          <option value="kml">KML</option>
        </select>
      </label>
      <label>{{ 'assetMgmt.fileLabel' | translate }}
        <input type="file" (change)="onImportFile($event)" accept=".geojson,.json,.kml" />
      </label>
      <label>{{ 'assetMgmt.payload' | translate }}
        <textarea [(ngModel)]="importPayload" name="importPayload" rows="10"></textarea>
      </label>
    </div>

    <div *ngIf="importResult" class="am-import-result">
      <strong>{{ importResult.succeeded }}</strong> / {{ importResult.total }}
      {{ 'assetMgmt.imported' | translate }}<span *ngIf="importResult.failed">,
      <strong class="danger">{{ importResult.failed }}</strong> {{ 'assetMgmt.failed' | translate }}</span>.
      <ul *ngIf="importResult.errors?.length">
        <li *ngFor="let e of importResult.errors">{{ e.message }}</li>
      </ul>
    </div>

    <div class="am-modal-foot">
      <button class="btn ghost" (click)="closeImportModal()">{{ 'common.close' | translate }}</button>
      <button class="btn primary" (click)="runImport()" [disabled]="saving">{{ 'assetMgmt.runImport' | translate }}</button>
    </div>
  </div>
</div>

<!-- Version history modal -->
<div class="am-modal" *ngIf="showVersionsModal" (click)="closeVersionsModal()">
  <div class="am-modal-box" (click)="$event.stopPropagation()">
    <h3>
      {{ 'assetMgmt.versionHistory' | translate }} —
      <code>{{ versionsAsset?.properties?.asset_code || versionsAsset?.id }}</code>
    </h3>
    <div *ngIf="loadingVersions" class="am-loading">{{ 'assetMgmt.loading' | translate }}…</div>
    <div *ngIf="!loadingVersions" class="am-table-wrap">
      <table class="am-table">
        <thead>
          <tr>
            <th>{{ 'assetMgmt.version' | translate }}</th>
            <th>{{ 'assetMgmt.changeType' | translate }}</th>
            <th>{{ 'assetMgmt.status' | translate }}</th>
            <th>{{ 'assetMgmt.summary' | translate }}</th>
            <th>{{ 'assetMgmt.changedAt' | translate }}</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          <tr *ngFor="let v of versions">
            <td>
              v{{ v.version }}
              <span class="chip" *ngIf="v.version === versionsAsset?.properties?.current_version">
                {{ 'assetMgmt.current' | translate }}
              </span>
            </td>
            <td>{{ v.change_type }}</td>
            <td><span class="chip status">{{ v.status }}</span></td>
            <td>{{ v.change_summary || '—' }}</td>
            <td>{{ v.changed_at | date: 'medium' }}</td>
            <td>
              <button
                class="link"
                *ngIf="versionsAsset && v.version !== versionsAsset.properties.current_version"
                (click)="rollbackAsset(versionsAsset, v.version)"
                [title]="'assetMgmt.rollback' | translate"
              >
                <i class="fa fa-history"></i> {{ 'assetMgmt.rollback' | translate }}
              </button>
            </td>
          </tr>
          <tr *ngIf="versions.length === 0">
            <td colspan="6" class="empty">{{ 'assetMgmt.noVersions' | translate }}</td>
          </tr>
        </tbody>
      </table>
    </div>
    <div class="am-modal-foot">
      <button class="btn ghost" (click)="closeVersionsModal()">{{ 'common.close' | translate }}</button>
    </div>
  </div>
</div>
